import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { CURRENCIES } from "@/types";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(amount: number, currencyCode: string = 'USD'): string {
  const currency = CURRENCIES.find(c => c.code === currencyCode) || CURRENCIES[0];
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function formatDate(date: string | Date): string {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(new Date(date));
}

export function formatDateTime(date: string | Date): string {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(date));
}

export function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

/**
 * Generates a temporary ID for UI components only (e.g., form item keys).
 * Uses crypto.randomUUID() for consistency with secure practices.
 * NOTE: For database IDs, always use Supabase's gen_random_uuid().
 */
export function generateId(): string {
  return crypto.randomUUID();
}

export function getStatusColor(status: string): string {
  switch (status) {
    case 'paid':
    case 'signed':
      return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
    case 'sent':
    case 'viewed':
      return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    case 'draft':
      return 'bg-muted text-muted-foreground border-border';
    case 'overdue':
    case 'expired':
      return 'bg-destructive/20 text-destructive border-destructive/30';
    case 'cancelled':
      return 'bg-muted text-muted-foreground border-border';
    default:
      return 'bg-muted text-muted-foreground border-border';
  }
}

export function calculateTaxAmount(amount: number, taxRate: number): number {
  return amount * (taxRate / 100);
}

export function getTrustScoreLabel(score: number): { label: string; color: string } {
  if (score >= 80) return { label: 'Excellent', color: 'text-emerald-400' };
  if (score >= 60) return { label: 'Good', color: 'text-blue-400' };
  if (score >= 40) return { label: 'Fair', color: 'text-amber-400' };
  return { label: 'Building', color: 'text-muted-foreground' };
}

export function getTaxVaultHealth(coverage: number): { label: string; color: string } {
  if (coverage >= 100) return { label: 'Healthy', color: 'text-emerald-400' };
  if (coverage >= 50) return { label: 'Moderate', color: 'text-amber-400' };
  return { label: 'Low', color: 'text-destructive' };
}
